/*
 * NOTE
 * The shelter's timezone, held outside the session store for the same reason as
 * `activeDateFormat`: `helpers/RULES.md` keeps helpers pure, so they can never import the session
 * store. The store WRITES here (`setActiveShelterTimezone`), everything else READS.
 *
 * Why any of this exists: a date picker that opens on "now" must open on the SHELTER's now. The
 * value it produces rides the wire timezone-naive and the API anchors it to the shelter, so a
 * browser-built `new Date()` recorded a remote staffer's own wall clock against the shelter —
 * three hours out from São Paulo to Denver, and a whole day out to Tokyo.
 *
 * NO TIMEZONE MEANS NO CHANGE. When the shelter has none stored, or it is unrecognised,
 * `getShelterNow()` returns a plain `new Date()` — exactly today's behaviour. This must never
 * invent a zone: guessing one would move every picker in the app for a shelter nobody configured.
 * Same shape as foster's `shelterNow()` in `AddWeightModal.vue`.
 *
 * Seeded SYNCHRONOUSLY at module load from the persisted profile, and a `ref` so shelter switching
 * (`AActionLoginAs`) takes effect without a reload.
 * */

import { ref } from 'vue';
import moment from 'moment-timezone';
import { readPersistedShelterProfile } from './shelterProfileStorage';

const activeShelterTimezone = ref<string | null>(getPersistedShelterTimezone());

// HELPERS
function isKnownTimezone(timezone: unknown): timezone is string {
  const isString = typeof timezone === 'string';

  return isString && !!moment.tz.zone(timezone);
}

function getPersistedShelterTimezone(): string | null {
  const shelterProfile = readPersistedShelterProfile();
  const storedTimezone = shelterProfile?.timezone;

  return isKnownTimezone(storedTimezone) ? storedTimezone : null;
}

function getActiveShelterTimezone(): string | null {
  return activeShelterTimezone.value;
}

/**
 * "Now" on the SHELTER's wall clock, as a `Date` a picker can bind to.
 *
 * The parts are re-assembled through the local `Date` constructor on purpose. A picker renders a
 * `Date` in the BROWSER's zone, so handing it the true instant would display the browser's clock
 * again. Rebuilding the shelter's wall-clock parts locally makes the picker show — and then send —
 * the shelter's own time. The value is therefore a wall clock, not an instant: do not subtract two
 * of these expecting a real duration.
 */
function getShelterNow(): Date {
  const timezone = activeShelterTimezone.value;
  if (!timezone) return new Date();

  const shelterMoment = moment().tz(timezone);

  return new Date(
    shelterMoment.year(),
    shelterMoment.month(),
    shelterMoment.date(),
    shelterMoment.hour(),
    shelterMoment.minute(),
    shelterMoment.second()
  );
}

// EVENTS
function setActiveShelterTimezone(timezone: unknown) {
  activeShelterTimezone.value = isKnownTimezone(timezone) ? timezone : null;
}

function resetActiveShelterTimezone() {
  activeShelterTimezone.value = null;
}

export { getActiveShelterTimezone, getShelterNow, resetActiveShelterTimezone, setActiveShelterTimezone };
